import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin, Instagram, MessageCircle } from 'lucide-react';
import { artistInfo } from '../../data/artworksData';

const Footer = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'ru' ? 'ru' : 'en';
  const currentYear = new Date().getFullYear();

  const navLinks = [
    { path: '/gallery', label: { ru: 'Галерея', en: 'Gallery' } },
    { path: '/about', label: { ru: 'О художнике', en: 'About' } },
    { path: '/contact', label: { ru: 'Контакты', en: 'Contact' } }
  ];

  const legalLinks = [
    { path: '/privacy', label: { ru: 'Политика конфиденциальности', en: 'Privacy Policy' } },
    { path: '/terms', label: { ru: 'Условия использования', en: 'Terms of Use' } }
  ];

  return (
    <footer className="bg-gallery-text text-white safe-area-inset-bottom">
      <div className="container mx-auto px-4 sm:px-6">
        {/* Main Footer - Mobile-first responsive grid */}
        <div className="py-10 sm:py-12 md:py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 md:gap-12">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <Link to="/" className="font-serif text-xl sm:text-2xl tracking-wide block mb-3 sm:mb-4">
              {artistInfo.name[lang]}
            </Link>
            <p className="text-white/60 text-sm leading-relaxed mb-5 sm:mb-6">
              {artistInfo.statement[lang].substring(0, 120)}...
            </p>
            {/* Social links - Touch-friendly 44px targets */}
            <div className="flex gap-3">
              <a
                href={artistInfo.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 border border-white/30 flex items-center justify-center hover:bg-white hover:text-gallery-text active:bg-white active:text-gallery-text transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={18} />
              </a>
              <a
                href={artistInfo.social.telegram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 border border-white/30 flex items-center justify-center hover:bg-white hover:text-gallery-text active:bg-white active:text-gallery-text transition-colors"
                aria-label="Telegram"
              >
                <MessageCircle size={18} />
              </a>
            </div>
          </div>

          {/* Navigation - Touch-friendly links */}
          <div>
            <h4 className="text-sm uppercase tracking-wider mb-4 sm:mb-6">
              {lang === 'ru' ? 'Навигация' : 'Navigation'}
            </h4>
            <ul className="space-y-1">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-white/60 hover:text-white active:text-white transition-colors text-sm py-2 block min-h-[44px] flex items-center"
                  >
                    {link.label[lang]}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact - Touch-friendly links */}
          <div>
            <h4 className="text-sm uppercase tracking-wider mb-4 sm:mb-6">
              {lang === 'ru' ? 'Контакты' : 'Contact'}
            </h4>
            <ul className="space-y-2">
              <li>
                <a
                  href={`mailto:${artistInfo.email}`}
                  className="flex items-center gap-3 text-white/60 hover:text-white active:text-white transition-colors text-sm py-2 min-h-[44px]"
                >
                  <Mail size={16} className="text-white/40 flex-shrink-0" />
                  <span className="break-all">{artistInfo.email}</span>
                </a>
              </li>
              <li>
                <a
                  href={`tel:${artistInfo.phone}`}
                  className="flex items-center gap-3 text-white/60 hover:text-white active:text-white transition-colors text-sm py-2 min-h-[44px]"
                >
                  <Phone size={16} className="text-white/40 flex-shrink-0" />
                  <span>{artistInfo.phone}</span>
                </a>
              </li>
              <li className="flex items-center gap-3 py-2">
                <MapPin size={16} className="text-white/40 flex-shrink-0" />
                <span className="text-white/60 text-sm">
                  {artistInfo.location[lang]}
                </span>
              </li>
            </ul>
          </div>

          {/* Newsletter / CTA */}
          <div>
            <h4 className="text-sm uppercase tracking-wider mb-4 sm:mb-6">
              {lang === 'ru' ? 'Следите за новостями' : 'Stay Updated'}
            </h4>
            <p className="text-white/60 text-sm mb-4">
              {lang === 'ru'
                ? 'Подпишитесь на обновления о новых работах и выставках'
                : 'Subscribe to updates about new works and exhibitions'
              }
            </p>
            {/* Form with touch-friendly inputs */}
            <form className="flex gap-2" onSubmit={(e) => e.preventDefault()}>
              <input
                type="email"
                placeholder="Email"
                className="flex-1 min-w-0 px-4 py-3 min-h-[44px] bg-white/10 border border-white/20 text-white placeholder-white/40 text-base focus:outline-none focus:border-white/40"
              />
              <button
                type="submit"
                className="px-4 py-3 min-h-[44px] bg-white text-gallery-text text-sm hover:bg-gallery-accent hover:text-white active:bg-gallery-accent active:text-white transition-colors flex-shrink-0"
              >
                {lang === 'ru' ? 'OK' : 'OK'}
              </button>
            </form>
          </div>
        </div>

        {/* Bottom Bar - Responsive layout */}
        <div className="border-t border-white/10 py-5 sm:py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-white/40 text-xs sm:text-sm text-center sm:text-left">
              &copy; {currentYear} {artistInfo.name[lang]}. {lang === 'ru' ? 'Все права защищены.' : 'All rights reserved.'}
            </p>
            {/* Legal links - Touch-friendly */}
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
              {legalLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="text-white/40 hover:text-white/60 active:text-white/60 transition-colors text-xs py-1 min-h-[32px] flex items-center"
                >
                  {link.label[lang]}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
